import type { OutboxEntry, StorageAdapter, SyncCursor, SyncedRow, SyncedTable, Uuid } from "@ainkauf/core";
import { db } from "@/db/dexie";

/**
 * Der Speicher-Adapter für den Browser. Übersetzt das Interface aus
 * packages/core auf Dexie. Die Engine selbst kennt Dexie nicht, damit sie
 * unter Expo mit expo-sqlite unverändert läuft.
 */
export class DexieStorage implements StorageAdapter {
  async readRows<T extends SyncedRow>(table: SyncedTable, ids: readonly Uuid[]): Promise<T[]> {
    const rows = await db.table<T>(table).bulkGet([...ids]);
    return rows.filter((row): row is T => row !== undefined);
  }

  async writeRows<T extends SyncedRow>(table: SyncedTable, rows: readonly T[]): Promise<void> {
    await db.table<T>(table).bulkPut([...rows]);
  }

  async enqueue(entry: OutboxEntry): Promise<void> {
    await db.transaction("rw", db.table(entry.table), db.outbox, async () => {
      await db.table(entry.table).put(entry.payload);
      await db.outbox.put(entry);
    });
  }

  async takeDue(now: string, limit: number): Promise<OutboxEntry[]> {
    return db.outbox.where("next_attempt_at").belowOrEqual(now).limit(limit).toArray();
  }

  async markSent(ids: readonly Uuid[]): Promise<void> {
    await db.outbox.bulkDelete([...ids]);
  }

  async markFailed(id: Uuid, error: string, nextAttemptAt: string): Promise<void> {
    const entry = await db.outbox.get(id);
    if (!entry) return;
    await db.outbox.put({ ...entry, attempts: entry.attempts + 1, last_error: error, next_attempt_at: nextAttemptAt });
  }

  async readCursor(table: SyncedTable): Promise<SyncCursor> {
    return (await db.cursors.get(table)) ?? { table, updated_at: null };
  }

  async writeCursor(cursor: SyncCursor): Promise<void> {
    await db.cursors.put(cursor);
  }

  async countPending(): Promise<number> {
    return db.outbox.count();
  }
}
